import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import SidebarAdmin from "./Utils/SidebarAdmin";

import "./Dashboard.css";
import "./CreateDrive.css";
import LogoutButton from "./LogoutButton";
import { AgGridReact } from "ag-grid-react";
import { apiFetch } from "../apis/apiFetch";
import apiConfig from "../config/apiConfig";
import { useRaspStore } from "../store/raspStore";
import { LoginContext } from "../context/LoginContext";
import WithdrawModel from "../models/WithdrawalsModel";
import { useQuery } from "@tanstack/react-query";
import { withdrawalsService } from "../services/WithdrawalService";
import { ColumnDef } from "./Resource/UpdateDrive";
import { queryAttachmentService } from "../services/RenderImageService";
import { toast } from "../utils/toast";
import JSZip from "jszip";
import * as XLSX from "xlsx";
const requiredFields = ["app_id", "name", "year", "programme", "email", "contact", "reason", "reason_desc", "g_creation_time", "withdrawal_letter_doc_id", "status"]

export default function Withdrawals() {
    const navigate = useNavigate();
    const { user } = useContext(LoginContext);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const [rowData, setRowData] = useState<any[]>([]);
    const [colDef1, setColDef1] = useState<any[]>([]);

    const defaultColDef = {
        flex: 1,
        minWidth: 100,
        editable: false,
    };

    const { data: dataRes, isLoading: isLoadingDataRes, error: errorDataRes, refetch } = useQuery({
        queryKey: ['resourceData', 'withdrawalsRead'],
        queryFn: async () => {
            const response = await withdrawalsService.getData()
            return response;
        },
    })
    const userId = user?.userId || "";
    const [isLoading, setIsLoading] = useState(false);
    const formatDate = (timestamp?: number | string) => {
        if (!timestamp) return "-";

        return new Date(Number(timestamp)).toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        }).replace(",", "");
    };

    const handelDownload = (docId: string, app_id: string) => {
        setIsLoading(true);
        queryAttachmentService.getAttachment(docId, userId)
            .then((blob) => {
                if (!blob) return;

                const url = URL.createObjectURL(blob);

                const a = document.createElement("a");
                a.href = url;
                a.download = `${app_id}_withdrawal_letter.pdf`; // Or use the actual filename
                document.body.appendChild(a);
                a.click();

                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            })
            .catch((err) => console.error("Error fetching document:", err))
            .finally(() => setIsLoading(false));
    }

    const handelUpdatePdf = async (withdrawalId: string, app_id: string) => {
        setIsLoading(true);
        try {
            const url = `${apiConfig.getResourceUrl("withdrawals")}/updatePdf?queryId=GET&appId=${app_id}&withdrawalId=${withdrawalId}`;

            const response = await apiFetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();

            if (data.success === true) {
                toast.success("Withdrawal Letter Updated.");
                refetch()
            } else {
                toast.error("Unable to update Withdrawal Letter.");
            }
        } catch (err) {
            toast.error("Unable to update Withdrawal Letter.");
            console.error("Error fetching document:", err)
        } finally {
            setIsLoading(false)
        }
    }

    const handleBulkZipDownload = async () => {
        if (rowData.length === 0) {
            toast.info("No withdrawal letters found.");
            return;
        }

        setIsLoading(true);

        try {
            const zip = new JSZip();

            const downloads = rowData
                .filter((item) => item.withdrawal_letter_doc_id)
                .map(async (withdrawal) => {
                    try {
                        const blob = await queryAttachmentService.getAttachment(
                            withdrawal.withdrawal_letter_doc_id,
                            userId
                        );

                        if (!blob) return;

                        zip.file(
                            `${withdrawal.app_id}_withdrawal_letter.pdf`,
                            blob
                        );
                    } catch (err) {
                        console.error(
                            `Failed to download ${withdrawal.app_id}`,
                            err
                        );
                    }
                });

            // Download all PDFs in parallel
            await Promise.all(downloads);

            // Generate ZIP
            const zipBlob = await zip.generateAsync({
                type: "blob",
                compression: "DEFLATE",
                compressionOptions: {
                    level: 9,
                },
            });

            const url = URL.createObjectURL(zipBlob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `Withdrawals.zip`;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(url);
            toast.success("Bulk download completed.");
        } catch (err) {
            console.error(err);
            toast.error("Unable to create ZIP file.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkFileDownload = async () => {
        if (rowData.length === 0) {
            toast.info("No withdrawal letters found.");
            return;
        }

        setIsLoading(true);

        try {
            for (const withdrawal of rowData) {
                if (!withdrawal.withdrawal_letter_doc_id) continue;

                try {
                    const blob = await queryAttachmentService.getAttachment(
                        withdrawal.withdrawal_letter_doc_id,
                        userId
                    );

                    if (!blob) continue;

                    const url = URL.createObjectURL(blob);

                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${withdrawal.app_id}_withdrawal_letter.pdf`;

                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);

                    URL.revokeObjectURL(url);

                    // optional delay
                    await new Promise((resolve) => setTimeout(resolve, 300));

                } catch (err) {
                    console.error(
                        `Failed to download ${withdrawal.app_id}`,
                        err
                    );
                }
            }

            toast.success("Bulk download completed.");
        } finally {
            setIsLoading(false);
        }
    };


    const handleExportExcel = () => {
        if (!rowData.length) {
            toast.info("No data available.");
            return;
        }

        const normalizeProg = (p: string) => {
            if (!p) return p;
            const str = p.toLowerCase().replace(/[^a-z]/g, "");
            if (str.includes("btechcse")) return "BTech_CSE";
            if (str.includes("btechece")) return "BTech_ECE";
            if (str.includes("btechaids") || str.includes("btechaiandds")) return "BTech_AI_DS";
            if (str.includes("imtechcse") || str.includes("integratedmtechcse")) return "IMTech_CSE";
            if (str.includes("imtechece") || str.includes("integratedmtechece")) return "IMTech_ECE";
            if (str.includes("mtechcse")) return "MTech_CSE";
            if (str.includes("mtechaids") || str.includes("mtechaiandds")) return "MTech_AI_DS";
            if (str.includes("mtechece")) return "MTech_ECE";
            return p;
        };

        const exportData = rowData.map((item) => ({
            "Application ID": item.app_id,
            Name: item.name,
            Year: item.year,
            Programme: normalizeProg(item.programme),
            Email: item.email,
            Contact: item.contact,
            Reason: item.reason,
            "Reason Description": item.reason_desc,
            "Created On": formatDate(item.g_creation_time),
            Status: item.status,
            "Bank Account Number": item.bank_acc_no,
            "Bank Account Name": item.bank_acc_name,
            "Bank Address": item.bank_address,
            "Bank IFSC Code": item.bank_ifsc,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Withdrawals"
        );

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        XLSX.writeFile(
            workbook,
            `Withdrawals.xlsx`
        );
    };

    const handleStatusChange = async (withdrawal: any, newStatus: string) => {
        try {
            const updateObj = {
                ...withdrawal,
                status: newStatus,
            };
            const jsonString = JSON.stringify(updateObj);
            const base64EncodeFun = (str: string) => btoa(unescape(encodeURIComponent(str)));
            const base64Encoded = base64EncodeFun(jsonString);

            await withdrawalsService.update(withdrawal.id, base64Encoded);

            // Update grid locally
            setRowData((prev) =>
                prev.map((item) =>
                    item.id === withdrawal.id
                        ? { ...item, status: newStatus }
                        : item
                )
            );

            // Or if you're using React Query:
            // queryClient.invalidateQueries({ queryKey: ['resourceData', 'withdrawalsRead'] });

        } catch (error) {
            console.error("Error updating withdrawal status:", error);
            toast.error("Failed to update status.");
        }
    };

    const StatusCellRenderer = (params: any) => {
        if (!params.value) return "-";
        const withdrawal = params.data;
        return (
            <select
                value={params.value}
                className={`status-open`}
                onChange={(e) => {
                    e.stopPropagation();
                    handleStatusChange(withdrawal, e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <option value="INITIATED" disabled>Initiated</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="APPROVED">Approved</option>
                <option value="PROCESSED">Processed</option>
            </select>
        );
    };

    useEffect(() => {
        const columns = requiredFields.map((field: any) => {
            const baseCol = {
                field,
                headerName:
                    field === "app_id"
                        ? "Application ID"
                        : field === "programme"
                            ? "Programme"
                            : field === "reason"
                                ? "Reason"
                                : field === "reason_desc"
                                    ? "Reason Description"
                                    : field === "status"
                                        ? "Status"
                                        : field === "g_creation_time"
                                            ? "Created On"
                                            : field === "withdrawal_letter_doc_id"
                                                ? "Withdrawal Letter"
                                                : field.charAt(0).toUpperCase() + field.slice(1),
                editable: false,
                resizable: true,
                sortable: true,
                filter: true,
            };

            if (field === "withdrawal_letter_doc_id") {
                return {
                    ...baseCol,
                    sortable: false,
                    filter: false,
                    width: 180,
                    cellRenderer: (params: any) => {
                        if (!params.value) return "-";
                        return (
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handelDownload(params.value, params.data.app_id)}
                            >
                                Download
                            </button>
                        );
                    },
                };
            }
            if (field === "g_creation_time") {
                return {
                    ...baseCol,
                    headerName: "Created On",
                    width: 180,
                    valueFormatter: (params: any) => {
                        if (!params.value) return "-";
                        return formatDate(params.value);
                    },
                };
            }
            // if (field === "update") {
            //     return {
            //         ...baseCol,
            //         sortable: false,
            //         filter: false,
            //         width: 180,
            //         cellRenderer: (params: any) => {
            //             // if (!params.value) return "-";
            //             return (
            //                 <button
            //                     className="btn btn-primary btn-sm"
            //                     onClick={() => handelUpdatePdf(params.data.id, params.data.app_id)}
            //                 >
            //                     Update PDF
            //                 </button>
            //             );
            //         },
            //     };
            // }
            if (field === "status") {
                return {
                    ...baseCol,
                    sortable: false,
                    filter: false,
                    width: 180,
                    cellRenderer: StatusCellRenderer,
                }
            }

            return baseCol;
        });

        setColDef1(columns);
    }, [requiredFields]);
    useEffect(() => {
        if (dataRes) {

            let dataToStore = { resource: 'withdrawals', records: dataRes }
            useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
            //ADDED: Convert array of plain objects → array of model instances
            const modelObjects = dataRes.map((obj: any) => WithdrawModel.fromJson(obj));

            // ADDED: Convert array of model instances → array of JSON objects (for ag-grid)
            const jsonObjects = modelObjects.map((model: any) => model.toJson());

            // ADDED: Set final array for AgGrid
            setRowData(jsonObjects);
        }
    }, [dataRes]);

    return (
        <div className="dash-layout">
            {/* Sidebar */}
            <SidebarAdmin
                sidebarCollapsed={sidebarCollapsed}
                toggleSidebar={() => setSidebarCollapsed((p) => !p)}
            />

            {/* Main Wrapper */}
            <div
                className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""
                    }`}
            >
                {/* Header */}
                <header className="dash-header">
                    <h1 className="dash-page-title">Withdrawals</h1>

                    <div className="dash-user-profile">
                        <div
                            className="dash-profile-circle"
                            onClick={() => setShowDropdown((p) => !p)}
                        >
                            <span>A</span>
                        </div>

                        {showDropdown && (
                            <div className="dash-profile-dropdown">
                                <LogoutButton />
                            </div>
                        )}
                    </div>
                </header>

                <div >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "10px",
                            margin: "15px",
                        }}
                    >
                        <button
                            className="btn btn-success"
                            onClick={handleExportExcel}
                            disabled={isLoading}
                        >
                            Export Excel
                        </button>

                        <button
                            className="btn btn-primary"
                            onClick={handleBulkZipDownload}
                            disabled={isLoading}
                        >
                            Bulk Download PDFs
                        </button>
                    </div>
                    {rowData.length === 0 && colDef1.length === 0 ? (
                        <div>No data available. Please add a resource attribute.</div>
                    ) : (
                        <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                            <AgGridReact
                                rowData={rowData}
                                columnDefs={colDef1}
                                defaultColDef={defaultColDef}
                                pagination={true}
                                paginationPageSize={10}
                                animateRows={true}
                                rowSelection="multiple"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
